/**
 * Permission Context - Server-Authoritative Permission System
 * Fetches permissions from /v1/users/me/permissions and provides them to the UI
 *
 * 5 Canonical Roles:
 * - owner: Full access + organization deletion + ownership transfer
 * - admin: Full access (no delete org, no transfer ownership)
 * - manager: Can invite users, manage own resources (NO role changes, NO user deletion)
 * - user: Regular member access (view + own resources)
 * - auditor: Read-only access for compliance/audit
 *
 * Performance Optimization:
 * - Uses React Query for caching with 5-minute stale time
 * - In-memory cache avoids flash on same-session navigation (NEVER localStorage — XSS risk)
 * - Shows cached data immediately while fetching fresh data in background
 */

import { createContext, useContext, useCallback, useMemo, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/api/client'
import { queryPolicies } from '@/shared/api/query-policies'
import { useAuth, SystemRole, isPlatformAdmin } from './unified-auth'
import { createLogger } from '@/shared/lib/logger'

const logger = createLogger('permission-context')

// SECURITY: PermissionSet contains userId, organizationId, role — NEVER cache in localStorage (XSS risk)
// Use in-memory cache for same-session flash prevention. React Query handles cross-navigation caching.
const PERMISSIONS_CACHE_TTL = 5 * 60 * 1000 // 5 minutes
let inMemoryPermissionsCache: { data: PermissionSet; timestamp: number } | null = null

function getCachedPermissions(): PermissionSet | null {
  if (!inMemoryPermissionsCache) return null
  if (Date.now() - inMemoryPermissionsCache.timestamp < PERMISSIONS_CACHE_TTL) {
    return inMemoryPermissionsCache.data
  }
  inMemoryPermissionsCache = null
  return null
}

function setCachedPermissions(permissions: PermissionSet): void {
  inMemoryPermissionsCache = { data: permissions, timestamp: Date.now() }
}

function clearCachedPermissions(): void {
  inMemoryPermissionsCache = null
}

// Types matching the backend PermissionSet response
export interface NavigationPermissions {
  dashboard: boolean
  organization: boolean
  members: boolean
  agents: boolean
  missions: boolean
  governance: boolean
  resources: boolean
  analytics: boolean
  settings: boolean
  billing: boolean
  audit: boolean
  // Answer keys, not pages (agion/Agion#2727): whether a read only some roles
  // of a page may make will be served. No sidebar entry renders for them.
  // auditEvidence: the organisation-wide audit trail reads (owner, admin, auditor).
  // administration: the admin-only reads (owner, admin).
  auditEvidence: boolean
  administration: boolean
  waitlist?: boolean
}

export interface ActionPermissions {
  // Note: Keys are camelCase because API client uses keysToCamel() transformation
  canDeleteOrganization: boolean
  canTransferOwnership: boolean
  canManageBilling: boolean
  canInviteMembers: boolean
  canChangeUserRoles: boolean
  canDeleteUsers: boolean
  isReadOnly: boolean
}

export interface ResourceCRUD {
  create: boolean
  read: boolean
  update: boolean
  delete: boolean
  export?: boolean
  scope: 'own' | 'all'
}

export interface ResourcePermissions {
  dashboard: ResourceCRUD
  organization: ResourceCRUD
  members: ResourceCRUD
  agents: ResourceCRUD
  missions: ResourceCRUD
  governance: ResourceCRUD
  resources: ResourceCRUD
  analytics: ResourceCRUD
  settings: ResourceCRUD
  billing: ResourceCRUD
  audit: ResourceCRUD
}

export interface PermissionSet {
  role: SystemRole
  // Note: Keys are camelCase because API client uses keysToCamel() transformation
  userId: string
  organizationId: string
  navigation: NavigationPermissions
  actions: ActionPermissions
  resources: ResourcePermissions
}

interface PermissionContextValue {
  // State
  permissions: PermissionSet | null
  isLoading: boolean
  error: string | null

  // Actions
  refreshPermissions: () => Promise<void>

  // Permission checks
  canNavigateTo: (page: keyof NavigationPermissions) => boolean
  canPerformAction: (action: keyof ActionPermissions) => boolean
  canAccessResource: (resource: keyof ResourcePermissions, action: keyof ResourceCRUD, ownerId?: string) => boolean

  // Role checks
  isOwner: () => boolean
  isAdmin: () => boolean
  isManager: () => boolean
  isUser: () => boolean
  isAuditor: () => boolean
  isReadOnly: () => boolean

  // Combined checks
  canManageMembers: () => boolean
  canInviteMembers: () => boolean
  canDeleteOrganization: () => boolean
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined)

// Default permissions for unauthenticated users
const defaultPermissions: PermissionSet = {
  role: 'user',
  userId: '',
  organizationId: '',
  navigation: {
    dashboard: false,
    organization: false,
    members: false,
    agents: false,
    missions: false,
    governance: false,
    resources: false,
    analytics: false,
    settings: false,
    billing: false,
    audit: false,
    auditEvidence: false,
    administration: false,
  },
  actions: {
    canDeleteOrganization: false,
    canTransferOwnership: false,
    canManageBilling: false,
    canInviteMembers: false,
    canChangeUserRoles: false,
    canDeleteUsers: false,
    isReadOnly: true,
  },
  resources: {
    dashboard: { create: false, read: false, update: false, delete: false, scope: 'own' },
    organization: { create: false, read: false, update: false, delete: false, scope: 'own' },
    members: { create: false, read: false, update: false, delete: false, scope: 'own' },
    agents: { create: false, read: false, update: false, delete: false, scope: 'own' },
    missions: { create: false, read: false, update: false, delete: false, scope: 'own' },
    governance: { create: false, read: false, update: false, delete: false, scope: 'own' },
    resources: { create: false, read: false, update: false, delete: false, scope: 'own' },
    analytics: { create: false, read: false, update: false, delete: false, scope: 'own' },
    settings: { create: false, read: false, update: false, delete: false, scope: 'own' },
    billing: { create: false, read: false, update: false, delete: false, scope: 'own' },
    audit: { create: false, read: false, update: false, delete: false, scope: 'own' },
  },
}

interface PermissionProviderProps {
  children: ReactNode
}

// Query key for React Query
const PERMISSIONS_QUERY_KEY = ['permissions', 'me'] as const

export function PermissionProvider({ children }: PermissionProviderProps) {
  const { isAuthenticated, user } = useAuth()

  // Fetch permissions using React Query with caching
  const {
    data: fetchedPermissions,
    isLoading: isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [...PERMISSIONS_QUERY_KEY, user?.id],
    queryFn: async (): Promise<PermissionSet> => {
      logger.debug('Fetching permissions from server')
      try {
        const response = await api.get<PermissionSet>('users/me/permissions')
        logger.info('Permissions fetched successfully', { role: response.role })
        // Cache in-memory for same-session flash prevention
        setCachedPermissions(response)
        return response
      } catch (err) {
        logger.error('Failed to fetch permissions', err as Error)
        // If server fetch fails, try to use cached permissions
        const cached = getCachedPermissions()
        if (cached) {
          logger.warn('Using cached permissions after fetch failure')
          return cached
        }
        // Fallback to client-side permissions based on user role
        if (user?.role) {
          logger.warn('Using client-side fallback permissions', { role: user.role })
          const fallback = getFallbackPermissions(user.role)
          return fallback
        }
        throw err
      }
    },
    enabled: isAuthenticated && !!user?.id,
    // Permissions don't change often; catalog policy gives 5-min stale
    // / 30-min gc with refetchOnMount=false so navigation never blocks
    // on a permission refetch.
    ...queryPolicies.catalog,
    // Use cached data as initial/placeholder while fetching
    placeholderData: () => getCachedPermissions() ?? undefined,
  })

  // Determine the permissions to use:
  // 1. Fetched data from server (if available)
  // 2. In-memory cached data (for instant display during same session)
  // 3. Null if neither available
  const permissions = fetchedPermissions ?? getCachedPermissions() ?? null

  // Only show loading state if we have no cached data at all
  // This prevents the sidebar from flashing "Loading..." on every page change
  const isLoading = isFetching && !permissions

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to fetch permissions') : null

  // Refresh permissions function for manual refresh
  const refreshPermissions = useCallback(async () => {
    clearCachedPermissions()
    await refetch()
  }, [refetch])

  // Permission check functions
  const canNavigateTo = useCallback(
    (page: keyof NavigationPermissions): boolean => {
      if (!permissions) return false
      // Waitlist is platform-admin only (Agion staff email domains)
      if (page === 'waitlist') {
        return permissions.navigation[page] === true && isPlatformAdmin(user)
      }
      return permissions.navigation[page] === true
    },
    [permissions, user?.email]
  )

  const canPerformAction = useCallback(
    (action: keyof ActionPermissions): boolean => {
      if (!permissions) return false
      return permissions.actions[action] === true
    },
    [permissions]
  )

  const canAccessResource = useCallback(
    (resource: keyof ResourcePermissions, action: keyof ResourceCRUD, ownerId?: string): boolean => {
      if (!permissions) return false

      const resourcePerms = permissions.resources[resource]
      if (!resourcePerms) return false

      // Check if action is allowed
      const hasPermission = resourcePerms[action] === true

      // If scope is 'all', permission applies to all resources
      if (resourcePerms.scope === 'all') {
        return hasPermission
      }

      // If scope is 'own', check ownership
      if (resourcePerms.scope === 'own' && ownerId) {
        return hasPermission && ownerId === permissions.userId
      }

      // For actions without ownership context
      return hasPermission
    },
    [permissions]
  )

  // Role check functions
  const isOwner = useCallback(() => permissions?.role === 'owner', [permissions])
  const isAdmin = useCallback(() => permissions?.role === 'admin', [permissions])
  const isManager = useCallback(() => permissions?.role === 'manager', [permissions])
  const isUser = useCallback(() => permissions?.role === 'user', [permissions])
  const isAuditor = useCallback(() => permissions?.role === 'auditor', [permissions])
  const isReadOnly = useCallback(() => permissions?.actions.isReadOnly === true, [permissions])

  // Combined checks
  const canManageMembers = useCallback(
    () => permissions?.actions.canChangeUserRoles === true || permissions?.actions.canDeleteUsers === true,
    [permissions]
  )

  const canInviteMembers = useCallback(
    () => permissions?.actions.canInviteMembers === true,
    [permissions]
  )

  const canDeleteOrganization = useCallback(
    () => permissions?.actions.canDeleteOrganization === true,
    [permissions]
  )

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      permissions,
      isLoading,
      error,
      refreshPermissions,
      canNavigateTo,
      canPerformAction,
      canAccessResource,
      isOwner,
      isAdmin,
      isManager,
      isUser,
      isAuditor,
      isReadOnly,
      canManageMembers,
      canInviteMembers,
      canDeleteOrganization,
    }),
    [
      permissions,
      isLoading,
      error,
      refreshPermissions,
      canNavigateTo,
      canPerformAction,
      canAccessResource,
      isOwner,
      isAdmin,
      isManager,
      isUser,
      isAuditor,
      isReadOnly,
      canManageMembers,
      canInviteMembers,
      canDeleteOrganization,
    ]
  )

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
}

// Hook to use permissions
export function usePermissions(): PermissionContextValue {
  const context = useContext(PermissionContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider')
  }
  return context
}

// Fallback permissions generator for when API is unavailable
function getFallbackPermissions(role: SystemRole): PermissionSet {
  const base = { ...defaultPermissions, role }

  switch (role) {
    case 'owner':
      return {
        ...base,
        navigation: {
          dashboard: true,
          organization: true,
          members: true,
          agents: true,
          missions: true,
          governance: true,
          resources: true,
          analytics: true,
          settings: true,
          billing: true,
          audit: true,
          auditEvidence: true,
          administration: true,
          waitlist: true,
        },
        actions: {
          canDeleteOrganization: true,
          canTransferOwnership: true,
          canManageBilling: true,
          canInviteMembers: true,
          canChangeUserRoles: true,
          canDeleteUsers: true,
          isReadOnly: false,
        },
        resources: createFullResourcePermissions(),
      }

    case 'admin':
      return {
        ...base,
        navigation: {
          dashboard: true,
          organization: true,
          members: true,
          agents: true,
          missions: true,
          governance: true,
          resources: true,
          analytics: true,
          settings: true,
          billing: true,
          audit: true,
          auditEvidence: true,
          administration: true,
        },
        actions: {
          canDeleteOrganization: false,
          canTransferOwnership: false,
          canManageBilling: true,
          canInviteMembers: true,
          canChangeUserRoles: true,
          canDeleteUsers: true,
          isReadOnly: false,
        },
        resources: createFullResourcePermissions(),
      }

    case 'manager':
      return {
        ...base,
        navigation: {
          dashboard: true,
          organization: true,
          members: true,
          agents: true,
          missions: true,
          governance: true,
          resources: true,
          analytics: true,
          settings: true,
          billing: false,
          audit: true, // Manager can view audit log per OpenFGA model
          auditEvidence: false, // the organisation-wide trail is owner, admin and auditor
          administration: false,
        },
        actions: {
          canDeleteOrganization: false,
          canTransferOwnership: false,
          canManageBilling: false,
          canInviteMembers: true,
          canChangeUserRoles: false, // Manager cannot change roles
          canDeleteUsers: false, // Manager cannot delete users
          isReadOnly: false,
        },
        resources: createManagerResourcePermissions(),
      }

    case 'user':
      return {
        ...base,
        navigation: {
          dashboard: true,
          organization: true,
          members: true,
          agents: false,
          missions: true,
          governance: true,
          resources: false,
          analytics: true,
          settings: false,
          billing: false,
          audit: false,
          auditEvidence: false,
          administration: false,
        },
        actions: {
          canDeleteOrganization: false,
          canTransferOwnership: false,
          canManageBilling: false,
          canInviteMembers: false,
          canChangeUserRoles: false,
          canDeleteUsers: false,
          isReadOnly: false,
        },
        resources: createUserResourcePermissions(),
      }

    case 'auditor':
      // Least-privilege compliance observer (must match the server PermissionSet
      // in auth-service user_handler_permissions.go). This offline fallback runs
      // only during a /users/me/permissions outage — keeping it in lockstep
      // prevents an outage from resurfacing access removed server-side.
      return {
        ...base,
        navigation: {
          dashboard: true,
          organization: true,
          members: true,
          agents: false, // operational surface — no access
          missions: true, // member-scoped (enforced at the orchestration API)
          governance: true, // governance controls (read-only)
          resources: false, // operational catalog — no access (server 403s it)
          analytics: true,
          settings: false,
          billing: false,
          audit: true, // primary function
          auditEvidence: true, // the organisation-wide trail
          administration: false,
        },
        actions: {
          canDeleteOrganization: false,
          canTransferOwnership: false,
          canManageBilling: false,
          canInviteMembers: false,
          canChangeUserRoles: false,
          canDeleteUsers: false,
          isReadOnly: true,
        },
        resources: createAuditorResourcePermissions(),
      }

    default:
      return defaultPermissions
  }
}

// Helper functions to create resource permissions
function createFullResourcePermissions(): ResourcePermissions {
  const full: ResourceCRUD = { create: true, read: true, update: true, delete: true, export: true, scope: 'all' }
  return {
    dashboard: full,
    organization: { ...full, delete: true }, // Owner-only delete handled by actions
    members: full,
    agents: full,
    missions: full,
    governance: full,
    resources: full,
    analytics: { ...full, create: false, update: false, delete: false },
    settings: { ...full, create: false, delete: false },
    billing: { ...full, create: false, delete: false },
    audit: { ...full, create: false, update: false, delete: false },
  }
}

function createManagerResourcePermissions(): ResourcePermissions {
  return {
    dashboard: { create: false, read: true, update: true, delete: false, scope: 'own' },
    organization: { create: false, read: true, update: false, delete: false, scope: 'all' },
    members: { create: true, read: true, update: false, delete: false, scope: 'all' }, // Can invite only
    agents: { create: true, read: true, update: true, delete: true, scope: 'own' },
    missions: { create: true, read: true, update: true, delete: true, scope: 'own' },
    governance: { create: true, read: true, update: true, delete: true, scope: 'own' },
    resources: { create: true, read: true, update: true, delete: true, scope: 'own' },
    analytics: { create: false, read: true, update: false, delete: false, export: true, scope: 'all' },
    settings: { create: false, read: true, update: false, delete: false, scope: 'all' },
    billing: { create: false, read: false, update: false, delete: false, scope: 'own' },
    audit: { create: false, read: true, update: false, delete: false, scope: 'all' },
  }
}

function createUserResourcePermissions(): ResourcePermissions {
  return {
    dashboard: { create: false, read: true, update: false, delete: false, scope: 'own' },
    organization: { create: false, read: true, update: false, delete: false, scope: 'all' },
    members: { create: false, read: true, update: false, delete: false, scope: 'all' },
    agents: { create: false, read: true, update: false, delete: false, scope: 'own' },
    missions: { create: true, read: true, update: true, delete: false, scope: 'own' },
    governance: { create: false, read: true, update: false, delete: false, scope: 'all' },
    resources: { create: false, read: true, update: false, delete: false, scope: 'own' },
    analytics: { create: false, read: true, update: false, delete: false, scope: 'own' },
    settings: { create: false, read: false, update: false, delete: false, scope: 'own' },
    billing: { create: false, read: false, update: false, delete: false, scope: 'own' },
    audit: { create: false, read: false, update: false, delete: false, scope: 'own' },
  }
}

function createAuditorResourcePermissions(): ResourcePermissions {
  const readOnly: ResourceCRUD = { create: false, read: true, update: false, delete: false, export: true, scope: 'all' }
  const noAccess: ResourceCRUD = { create: false, read: false, update: false, delete: false, export: false, scope: 'own' }
  // Missions: read-only AND member-scoped (auditor sees only missions it was added to).
  const missionsRO: ResourceCRUD = { create: false, read: true, update: false, delete: false, export: true, scope: 'own' }
  return {
    dashboard: readOnly,
    organization: readOnly,
    members: readOnly,
    agents: noAccess, // operational surface
    missions: missionsRO,
    governance: readOnly, // governance controls
    resources: noAccess, // operational catalog (server 403s it)
    analytics: readOnly,
    settings: noAccess,
    billing: noAccess,
    audit: readOnly, // primary function
  }
}

// Export for testing and logout flow
export { defaultPermissions, getFallbackPermissions, clearCachedPermissions }
